import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { WorkflowConfigState } from '../../shared/state/workflow/workflow-config.state';
import { Store } from '@ngxs/store';
import {
    ClearSelectedWorkflowConfig,
    LoadSelectedWorkflowConfig,
    LoadWorkflowConfigs
} from '../../shared/state/workflow/workflow-config.actions';
import { ClearSnowflakeSearch } from '../../shared/state/snowflake/snowflake-search.actions';
import { WorkflowOptionSetService } from '../../shared/services/workflow-option-set.service';
import { WorkflowConfig, WorkflowConfigFilter, WorkflowConfigType } from '@monorepo/interfaces';
import { distinctUntilChanged, filter, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { CreateWorkflowOperations } from './create-workflow.operations';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { WorkflowConfigDetailsComponent } from '../workflow-config-details/workflow-config-details.component';
import { DialogRef, DialogService, SnackbarService, StepperComponent, StepperStep } from '@siemens/component-lib';
import { WorkflowComponent } from '../workflow-details/workflow/workflow.component';
import { catchError } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogData } from '../../shared/interfaces/miscellaneous/ConfirmationDialogData';
import { MessageOperationType } from '../../shared/enums/MessageOperationType.enum';
import { SapSearchProgressComponent } from '../../shared/components/sap-search-progress/sap-search-progress.component';

@Component({
    selector: 'app-create-workflow',
    templateUrl: './create-workflow.component.html',
    imports: [
        TranslatePipe,
        WorkflowConfigDetailsComponent,
        StepperComponent,
        WorkflowComponent,
        SapSearchProgressComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateWorkflowComponent implements OnInit, OnDestroy {
    private store = inject(Store);
    private translateService = inject(TranslateService);
    private snackbarService = inject(SnackbarService);
    private dialogRef = inject(DialogRef);
    private dialog = inject(DialogService);
    private workflowOptionSetService = inject(WorkflowOptionSetService);

    protected workflowTypes$ = this.store.select<WorkflowConfig[]>(WorkflowConfigState.configs);

    protected workflowConfigFormGroup: FormGroup = new FormGroup({});
    protected workflowTypes = signal<WorkflowConfig[]>([]);
    protected activeStep = signal<number>(0);
    protected activeSection = signal<number>(0);
    protected steps = signal<StepperStep[]>(CreateWorkflowOperations.initiialWorkflowSteps());
    protected readonly disabledSteps = signal<number[]>([1, 2, 3, 4]);
    protected readonly selectedWorkflowConfigId = signal<string>(null);

    protected readonly selectedWorkflowConfig = computed(
        () =>
            this.workflowTypes() &&
            this.workflowTypes().find(workflowType => workflowType.id === this.selectedWorkflowConfigId())
    );

    private destroy$: Subject<boolean> = new Subject<boolean>();

    ngOnInit(): void {
        this.loadWorkflowTypes();
        this.subscribeToWorkflowTypes();
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.store.dispatch(new ClearSelectedWorkflowConfig());
        this.store.dispatch(new ClearSnowflakeSearch());
        this.workflowOptionSetService.clearAllFieldOptions();
    }

    protected setStep(step: number): void {
        this.activeStep.set(step);
        this.activeSection.set(-1);
    }

    protected setSection(section: number): void {
        this.activeSection.set(section);
    }

    protected onDialogClose(): void {
        this.dialog
            .open<boolean, ConfirmationDialogData>(
                ConfirmationDialogComponent,
                CreateWorkflowOperations.getConfirmationDialogData()
            )
            .afterClosed.pipe(
                filter((result: boolean) => !!result),
                tap(() => this.dialogRef.close()),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private loadWorkflowTypes(): void {
        this.store
            .dispatch(
                new LoadWorkflowConfigs(WorkflowConfigType.COMMERCIAL_WORKFLOW, WorkflowConfigFilter.LATEST_VERSION)
            )
            .pipe(
                catchError(() =>
                    this.showErrorMessage(
                        'workflow.error.failed-to-load-workflow-types.title',
                        'workflow.error.failed-to-load-workflow-types.description'
                    )
                ),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private subscribeToWorkflowTypes(): void {
        this.workflowTypes$
            .pipe(
                filter(workflowTypes => !!workflowTypes && workflowTypes.length > 0),
                tap(workflowTypes => this.initializeWorkflowTypesAndBuildForm(workflowTypes)),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private initializeWorkflowTypesAndBuildForm(workflowTypes: WorkflowConfig[]): void {
        this.workflowConfigFormGroup = CreateWorkflowOperations.buildWorkflowDetailsForm(true);
        this.workflowTypes.set(workflowTypes);
        this.subscribeToWorkflowConfigSelected();
    }

    private subscribeToWorkflowConfigSelected(): void {
        this.workflowConfigFormGroup
            .get('id')
            ?.valueChanges.pipe(
                distinctUntilChanged(),
                switchMap((configId: string) => this.setConfigIdAndLoadWorkflowConfig(configId)),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private setConfigIdAndLoadWorkflowConfig(configId: string): Observable<void | boolean> {
        this.selectedWorkflowConfigId.set(configId);
        return this.store
            .dispatch(new LoadSelectedWorkflowConfig(this.workflowConfigFormGroup.get('type').value, configId))
            .pipe(
                catchError(() =>
                    this.showErrorMessage(
                        'workflow.error.failed-to-load-workload-config-type.title',
                        'workflow.error.failed-to-load-workload-config-type.description'
                    )
                )
            );
    }

    private showErrorMessage(title: string, description: string): Observable<boolean> {
        this.snackbarService.open(
            CreateWorkflowOperations.getSnackbarMessageConfig(
                MessageOperationType.ERROR,
                title,
                description,
                this.translateService
            )
        );
        this.dialogRef.close();
        return of(false);
    }
}
